/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from 'react'

import { Group } from '@/entities/groups/models/Group'
import { CurrentScreenType } from '@/entities/userInterface/model/CurrentScreenType'
import { PLANNING_TREE_EXPENDED_KEYS_LOCAL_STORAGE_KEY, RUNSHEET_TREE_EXPENDED_KEYS_LOCAL_STORAGE_KEY } from '@/shared/config/consts'
import { useLocalStorage } from '@/shared/lib/useLocalStorage'
import { EntityId } from '@reduxjs/toolkit'

export type TreeState = {
  expandedItems: string[]
}

export type StorageGroupTreeStateMapping = null | {
  [groupId: EntityId]: TreeState
}

export type OperationTreeState = {
  treeState: TreeState | null
  setTreeState: (state: TreeState) => void
}

/**
 * The following hook is used to manage the expansion of the tree view.
 *  It stores the tree expansion state in local storage.
 * 
 * @param groupId the current active groupId retrieved from the redux store
 * @param currentScreen either 'planning' or 'runsheet'
 * @returns OperationTreeState
 */
export const useOperationTreeState = (groupId: Group['id'], currentScreen: CurrentScreenType): OperationTreeState => {
  const isPlanning = currentScreen === 'planning'
  const [storageTreeStates, setStorageTreeStates] = useLocalStorage<StorageGroupTreeStateMapping>(
    isPlanning ? PLANNING_TREE_EXPENDED_KEYS_LOCAL_STORAGE_KEY : RUNSHEET_TREE_EXPENDED_KEYS_LOCAL_STORAGE_KEY
    , null)
  const [treeState, setLocalTreeState] = useState<TreeState | null>(storageTreeStates?.[groupId] || null)
  
  // SideEffect when groupId changes
  useEffect(() => {
    setLocalTreeState(storageTreeStates?.[groupId] || null)
  }, [groupId])

  return {
    treeState,
    setTreeState: (state: TreeState) => {
      setLocalTreeState(state)
      setStorageTreeStates({
        ...storageTreeStates,
        [groupId]: state,
      })
    },
  }
}
